// Coach-Governed Continuity Loop — "Active for next session" section.
// Shows coach-approved carried-forward items at the top of session prep.
// Coach-first rule: Never surface these outputs to leaders.
// This is retrieval only — no writes in this component.
import { Badge } from "@/components/ui/badge";
import type { ContinuityReview } from "@/lib/behavior/coachContinuityReviewService";

interface ActiveForNextSessionSectionProps {
  reviews: ContinuityReview[];
  mode?: "leader" | "coach_practice";
}

export function ActiveForNextSessionSection({ reviews, mode = "leader" }: ActiveForNextSessionSectionProps) {
  // Defensive filter: only render carried_forward items even if caller passes extras.
  const active = reviews.filter((r) => r.reviewState === "carried_forward");

  if (active.length === 0) return null;

  const sectionLabel =
    mode === "coach_practice" ? "Active in my practice" : "Active for next session";
  const sectionSubtitle =
    mode === "coach_practice"
      ? "Practice threads you've chosen to carry forward."
      : "Coach-approved moments to hold in mind going into this session.";
  const badgeLabel = mode === "coach_practice" ? "Active in practice" : "Carried forward";

  return (
    <div className="space-y-2 px-4 py-3 border-t border-border/70">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {sectionLabel}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {sectionSubtitle}
        </p>
      </div>
      <div className="space-y-2">
        {active.map((review) => {
          const title = review.reframedTitle ?? review.originalTitle;
          const reviewedDate = review.reviewedAt
            ? new Date(review.reviewedAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : null;

          return (
            <div
              key={review.id}
              className="rounded-lg border border-border bg-card p-3 space-y-1.5"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-foreground leading-snug">{title}</p>
                <Badge
                  variant="outline"
                  className="shrink-0 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[10px]"
                >
                  {badgeLabel}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {review.sourceType}
                {reviewedDate ? ` · ${reviewedDate}` : ""}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
